import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Phone, ShieldCheck, Gift } from "lucide-react";
import {
  heroSlides,
  featureCards,
  aboutIntro,
  services,
  whyChoose,
  stats,
  company,
} from "../mock";
import { iconMap } from "../lib/iconMap";
import Reveal from "../components/Reveal";

function Hero() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % heroSlides.length), 6000);
    return () => clearInterval(t);
  }, []);
  const s = heroSlides[i];

  return (
    <section className="relative min-h-[600px] md:min-h-[660px] flex items-center overflow-hidden">
      {heroSlides.map((slide, idx) => (
        <div
          key={idx}
          className="absolute inset-0 transition-opacity duration-1000"
          style={{ opacity: idx === i ? 1 : 0 }}
        >
          <img src={slide.image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
      ))}

      <div className="container-x relative z-10">
        <div className="max-w-2xl text-white" key={i}>
          <div className="flex w-fit items-center gap-2 rounded-full bg-[#D4AF37] text-[#0B1F3A] px-4 py-1.5 text-[13px] font-bold mb-5 shadow-lg reveal in-view" data-testid="hero-free-consult">
            <Gift size={15} /> {company.freeConsult}
          </div>
          <div className="eyebrow text-[#E4C55E] mb-4 reveal in-view">{s.eyebrow}</div>
          <h1 className="font-head text-4xl md:text-6xl font-extrabold leading-[1.05] tracking-tight reveal in-view">
            {s.title}
          </h1>
          <p className="mt-5 text-slate-200 text-lg max-w-xl leading-relaxed reveal in-view" style={{ animationDelay: "120ms" }}>
            {s.text}
          </p>
          <div className="mt-8 flex flex-wrap gap-4 reveal in-view" style={{ animationDelay: "220ms" }}>
            <Link to={s.cta.to} className="btn-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-lg text-[15px]">
              {s.cta.label} <ArrowRight size={18} />
            </Link>
            <Link to={s.cta2.to} className="btn-outline-light inline-flex items-center gap-2 px-7 py-3.5 rounded-lg text-[15px]">
              {s.cta2.label}
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex gap-2">
        {heroSlides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setI(idx)}
            className={`h-1.5 rounded-full transition-all ${idx === i ? "w-8 bg-[#D4AF37]" : "w-3 bg-white/40"}`}
            aria-label={`Slide ${idx + 1}`}
          />
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div>
      <Hero />

      {/* Feature cards */}
      <section className="relative z-20 -mt-16">
        <div className="container-x">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {featureCards.map((f, idx) => {
              const Icon = iconMap[f.icon];
              return (
                <Reveal key={f.title} delay={idx * 90}>
                  <div className="card-lift bg-white rounded-2xl p-6 border border-slate-100 shadow-[0_20px_50px_-30px_rgba(10,26,52,.35)] h-full">
                    <div className="w-12 h-12 rounded-xl bg-[#F6F7F9] grid place-items-center text-[#184E9E] mb-4">
                      <Icon size={24} />
                    </div>
                    <h3 className="font-head font-semibold text-[#0B1F3A] text-lg mb-2">{f.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{f.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* About intro */}
      <section className="py-20 md:py-24">
        <div className="container-x grid lg:grid-cols-2 gap-12 items-center">
          <Reveal className="relative">
            <div className="rounded-2xl overflow-hidden shadow-[0_30px_60px_-30px_rgba(10,26,52,.5)]">
              <img src={aboutIntro.image} alt="Our team" className="w-full h-[420px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -right-4 md:right-8 bg-[#0B1F3A] text-white rounded-2xl px-7 py-5 shadow-xl float-slow">
              <div className="font-head text-4xl font-extrabold text-[#D4AF37]">{company.yearsInBusiness}+</div>
              <div className="text-sm text-slate-300">Years in Business</div>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="eyebrow mb-4">{aboutIntro.eyebrow}</div>
            <h2 className="font-head text-3xl md:text-4xl font-bold text-[#0B1F3A] leading-tight">
              {aboutIntro.title}
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">{aboutIntro.body}</p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3">
              {aboutIntro.points.map((p) => (
                <li key={p} className="flex items-center gap-2.5 text-[15px] text-slate-700">
                  <CheckCircle2 size={19} className="text-[#184E9E] shrink-0" /> {p}
                </li>
              ))}
            </ul>
            <Link to="/about" className="btn-gold inline-flex items-center gap-2 px-6 py-3 rounded-lg mt-8">
              More About Us <ArrowRight size={17} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 md:py-24 bg-[#F6F7F9]">
        <div className="container-x">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <div className="eyebrow justify-center mb-4" style={{ display: "inline-flex" }}>Our Services & Solutions</div>
            <h2 className="font-head text-3xl md:text-4xl font-bold text-[#0B1F3A] leading-tight">
              We Increase Your B-BBEE Score While Driving Real Transformation
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, idx) => {
              const Icon = iconMap[s.icon];
              return (
                <Reveal key={s.slug} delay={(idx % 3) * 90}>
                  <Link to="/services" className="group block card-lift bg-white rounded-2xl p-7 border border-slate-100 h-full">
                    <div className="w-14 h-14 rounded-xl bg-[#0B1F3A] grid place-items-center text-[#D4AF37] mb-5 group-hover:bg-[#D4AF37] group-hover:text-[#0B1F3A] transition-colors">
                      <Icon size={26} />
                    </div>
                    <h3 className="font-head font-semibold text-[#0B1F3A] text-xl mb-2">{s.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed mb-4">{s.short}</p>
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#184E9E]">
                      Read More <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why choose + stats */}
      <section className="py-20 md:py-24">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="eyebrow mb-4">{whyChoose.eyebrow}</div>
            <h2 className="font-head text-3xl md:text-4xl font-bold text-[#0B1F3A] leading-tight">
              {whyChoose.title}
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">{whyChoose.body}</p>
            <ul className="mt-6 space-y-3">
              {whyChoose.points.map((p) => (
                <li key={p} className="flex items-start gap-3 text-[15px] text-slate-700">
                  <CheckCircle2 size={20} className="text-[#184E9E] shrink-0 mt-0.5" /> {p}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={120} className="grid grid-cols-2 gap-5">
            {stats.map((st) => (
              <div key={st.label} className="rounded-2xl bg-[#F6F7F9] p-7 text-center card-lift">
                <div className="font-head text-4xl md:text-5xl font-extrabold text-[#0B1F3A]">
                  {st.value}<span className="text-[#D4AF37]">{st.suffix}</span>
                </div>
                <div className="divider-gold mx-auto my-3" />
                <div className="text-sm text-slate-500">{st.label}</div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* Trusted & confidential note */}
      <section className="py-16 bg-[#0B1F3A]">
        <div className="container-x">
          <Reveal className="max-w-3xl mx-auto text-center">
            <div className="w-14 h-14 rounded-xl bg-white/5 border border-white/10 grid place-items-center text-[#D4AF37] mx-auto mb-5">
              <ShieldCheck size={26} />
            </div>
            <div className="eyebrow justify-center mb-3" style={{ display: "inline-flex", color: "#D4AF37" }}>Trusted &amp; Confidential</div>
            <p className="text-slate-200 text-lg leading-relaxed">
              We proudly serve organisations across South Africa's major sectors — from pharmaceuticals
              and technology to engineering and professional services. Out of respect for our clients'
              confidentiality, verifiable references are gladly provided on request during your consultation.
            </p>
            <Link to="/sectors" className="inline-flex items-center gap-2 mt-7 text-[#D4AF37] font-semibold hover:underline">
              See the sectors we serve <ArrowRight size={17} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Quote CTA banner */}
      <section className="py-20">
        <div className="container-x">
          <div className="rounded-3xl bg-gradient-to-br from-[#F6F7F9] to-white border border-slate-100 p-10 md:p-14 text-center">
            <div className="eyebrow justify-center mb-4" style={{ display: "inline-flex" }}>Ready to get started?</div>
            <h2 className="font-head text-3xl md:text-4xl font-bold text-[#0B1F3A] max-w-2xl mx-auto leading-tight">
              Request a tailored B-BBEE consulting quote in under a minute
            </h2>
            <p className="mt-4 text-slate-600 max-w-xl mx-auto">
              Share a few details about your business and our specialists will prepare pricing tailored specifically to your requirements. <span className="font-semibold text-[#0B1F3A]">And your first consultation is completely free of charge.</span>
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link to="/pricing-calculator" className="btn-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-lg">
                Request a Quote <ArrowRight size={18} />
              </Link>
              <a
                href={`tel:${company.phones[0].replace(/\s/g, "")}`}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg border border-slate-200 text-[#0B1F3A] font-semibold hover:border-[#D4AF37] transition-colors"
              >
                <Phone size={18} /> {company.phones[0]}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
